import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/clerk-react";
import { api, buildQueryString } from "@/lib/api";
import type {
  AssetsResponse,
  AssetDeleteResponse,
  AssetFavoriteResponse,
  AssetSource,
} from "@/types";

interface UseAssetsOptions {
  page?: number;
  perPage?: number;
  source?: AssetSource;
  /**
   * When true (default) hides character-bound generations. Pass false
   * from the Characters tab to opt out. Required to be `false` when
   * `folder` is provided.
   */
  bare?: boolean;
  /** Scope to a single character's folder (implies bare=false). */
  folder?: string | null;
}

export function useAssets(options: UseAssetsOptions = {}) {
  const { user, isLoaded } = useUser();
  const { page = 1, perPage = 20, source, bare = true, folder } = options;

  return useQuery({
    queryKey: [
      "assets",
      user?.id,
      { page, perPage, source, bare, folder: folder ?? null },
    ],
    queryFn: async () => {
      if (!user?.id) throw new Error("No user ID");
      const queryString = buildQueryString({
        page,
        per_page: perPage,
        source,
        bare: bare ? undefined : "false",
        folder: folder ?? undefined,
      });
      return api.get<AssetsResponse>(`/asset/${user.id}${queryString}`);
    },
    enabled: isLoaded && !!user?.id,
    staleTime: Infinity, // Never auto-refetch (only on mount, manual refresh, or invalidation)
    refetchOnWindowFocus: false, // Don't refetch on tab switch
    refetchOnMount: true, // Do refetch on page load
    refetchOnReconnect: false, // Don't refetch on reconnect
  });
}

export function useDeleteAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assetId: string) => {
      return api.post<AssetDeleteResponse>(`/asset/${assetId}/delete`);
    },
    // Optimistic update - remove asset immediately
    onMutate: async (assetId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["assets"] });

      // Snapshot all asset queries for rollback
      const previousQueries = queryClient.getQueriesData<AssetsResponse>({
        queryKey: ["assets"],
      });

      // Optimistically remove the asset from all cached queries
      queryClient.setQueriesData<AssetsResponse>(
        { queryKey: ["assets"] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            assets: old.assets.filter((asset) => asset.id !== assetId),
            pagination: {
              ...old.pagination,
              total: old.pagination.total - 1,
            },
          };
        },
      );

      return { previousQueries };
    },
    // Rollback on error
    onError: (_err, _assetId, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    // Refetch after success to ensure consistency
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
    },
  });
}

export function useFavoriteAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assetId: string) => {
      return api.post<AssetFavoriteResponse>(`/asset/${assetId}/favorite`);
    },
    // Optimistic update - toggle favorite immediately
    onMutate: async (assetId) => {
      await queryClient.cancelQueries({ queryKey: ["assets"] });

      const previousQueries = queryClient.getQueriesData<AssetsResponse>({
        queryKey: ["assets"],
      });

      // Optimistically toggle the favorite status
      queryClient.setQueriesData<AssetsResponse>(
        { queryKey: ["assets"] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            assets: old.assets.map((asset) =>
              asset.id === assetId
                ? { ...asset, is_favorite: !asset.is_favorite }
                : asset,
            ),
          };
        },
      );

      return { previousQueries };
    },
    onError: (_err, _assetId, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
    },
  });
}

export function useDownloadAsset() {
  return useMutation({
    mutationFn: async (assetId: string) => {
      // For downloads, we need to trigger browser download
      const url = `${import.meta.env.VITE_API_BASE_URL || "http://localhost:3001"}/asset/${assetId}/download`;

      // Create a temporary link and click it to trigger download
      const link = document.createElement("a");
      link.href = url;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return { success: true };
    },
  });
}
